import type { ISceneEntity, PieceButton, PieceButtonHole } from '@seddi/scene';
import { type Mesh, Sphere, Vector3 } from '@seddi/three';

import { PropsDiff } from '../core/reconciler/propsDiff';
import {
  type PieceButtonRenderable,
  attachButton,
  createStitches,
  disposeStitches,
  getParentId,
  isCloseToHole,
  updateTransformFromGarment,
  updateTransformFromPiece,
} from '../renderables/pieceButtonRenderable';
import { Scene } from '../scene/scene';
import type { SceneReconcilerFrame } from './sceneReconcilerFrame';

type ParentRenderable = { mesh: Mesh };

const InfinitySphere = new Sphere(new Vector3(), Infinity);

/**
 * Update renderable.
 * @param renderable - Renderable.
 * @param nextProps - Button next.
 * @param prevProps - Button prev.
 * @param pieceRenderable - Piece renderable.
 * @param frame - Frame.
 */
export const update1st = (
  renderable: PieceButtonRenderable,
  nextProps: PieceButton,
  prevProps: PieceButton | undefined,
  pieceRenderable: ParentRenderable,
  frame: SceneReconcilerFrame,
): boolean => {
  const { config, garmentGeometry, nextParent, nextScene, resources } = frame;

  const isGeometryDiffDw = !!renderable.garmentGeometry && !garmentGeometry;
  const isGeometryDiffUp = !renderable.garmentGeometry && !!garmentGeometry;
  const isGeometryAvailable = !!garmentGeometry;
  const entityId = getParentId(renderable);
  const isParentGeometryDirty = frame.entities.get(entityId)!.isGeometryDirty;

  const diffProps = PropsDiff.diff(nextProps, prevProps);
  let dirty = false;

  // Update resource.
  if (diffProps.includes('resourceID')) {
    const id = nextProps.resourceID;

    if (resources.findButton(id)) {
      attachButton(renderable, nextProps, resources);
      dirty = true;
    } else {
      if (!resources.hasButtonRequest(id)) {
        const b = Scene.findButton(nextScene, id);
        resources.loadButtonDeferred(b);
      }
    }
  }

  // Update transform.
  if (isGeometryDiffUp || isGeometryAvailable) {
    // Update transform from garment geometry.
    updateTransformFromGarment(renderable, garmentGeometry);
  } else if (
    ['resourceID', 'transform'].some(
      (key) => (!isGeometryAvailable && diffProps.includes(key)) || isGeometryDiffDw || isParentGeometryDirty,
    )
  ) {
    // Update transform depends on piece geometry and button height.
    const resource = Scene.findButton(nextScene, nextProps.resourceID)!;
    updateTransformFromPiece(renderable, nextProps, resource, pieceRenderable, nextParent);
  }

  // Update stitches
  if (!config.hideStitches) {
    // Create stitches from garment geometry.
    if (isGeometryDiffUp || isGeometryAvailable) {
      disposeStitches(renderable);
      createStitches(renderable, nextProps.recolorThread, garmentGeometry, resources);
      // Configure frustum culling
      {
        const { frustumCulling } = config;
        if (!frustumCulling) {
          for (const stitch of renderable.stitches) {
            stitch.mesh.frustumCulled = frustumCulling;
            // TODO: use scene boundingBox if available.
            stitch.mesh.geometry.boundingSphere = InfinitySphere;
          }
        }
      }
      dirty = true;
    }
    // Dispose stitches from garment geometry.
    if (isGeometryDiffDw) {
      disposeStitches(renderable);
      dirty = true;
    }
  }

  renderable.garmentGeometry = garmentGeometry;
  return true;
};

/**
 * Update renderable.
 * @param renderable - Renderable.
 * @param nextProps - Button next.
 * @param prevProps - Button prev.
 * @param pieceRenderable - Piece renderable.
 * @param frame - Frame.
 */
export const update2nd = (
  renderable: PieceButtonRenderable,
  nextProps: PieceButton,
  pieceRenderable: ParentRenderable,
  frame: SceneReconcilerFrame,
): boolean => {
  const { garmentGeometry, nextParent, nextScene } = frame;
  // Do nothing if garment geometry.
  if (garmentGeometry) {
    return false;
  }
  // Do nothing if no dirty pair.
  const pair = nextProps.pair;
  const pairData = pair && findPair(pair, frame);
  if (!pairData) {
    return false;
  }

  const pairEntity = pairData.entity;
  const pairHole = pairData.hole;
  const pairRenderable: ParentRenderable = frame.scene.entities.get(pairEntity._id)!.piece;
  const pairMesh = pairRenderable.mesh;
  if (
    isCloseToHole(
      nextParent.components.garmentPiece!,
      pieceRenderable.mesh,
      nextProps.transform,
      pairEntity.components.garmentPiece!,
      pairMesh,
      pairHole.transform,
    )
  ) {
    const resource = Scene.findButton(nextScene, nextProps.resourceID)!;
    updateTransformFromPiece(renderable, pairHole, resource, pairRenderable, pairEntity);
  }
};

// Detail

type HoleData = { entity: ISceneEntity; hole: PieceButtonHole };

/**
 * Find hole by ID.
 * @param id - Piece hole ID.
 * @param frame - Reconciler frame.
 */
const findPair = (id: string, frame: SceneReconcilerFrame): HoleData | undefined => {
  for (const e of frame.entities.values()) {
    if (!e.isGeometryDirty || !e.isPiece) {
      continue;
    }
    // Find corresponding hole.
    const entity = e.data;
    const hole = entity.components.garmentPiece.buttonHoles?.find((h) => h._id === id);
    if (hole) {
      return { entity, hole };
    }
  }
  return undefined;
};
